import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  CreatePaymentPlanClient,
  DeletePaymentPlanClient,
  UpdatePaymentPlanClient,
  SearchPaymentPlanUnscheduledClient,
  SearchPaymentPlanForExpenseInvoiceClient,
  CreateExpensePaymentPlanClient,
  SearchPaymentPlanForSaleInvoiceClient,
  CreateSalePaymentPlanClient,
  GetPaymentPlanClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const createPaymentPlanClient = new CreatePaymentPlanClient(SERVICE_URL)
const deletePaymentPlanClient = new DeletePaymentPlanClient(SERVICE_URL)
const updatePaymentPlanClient = new UpdatePaymentPlanClient(SERVICE_URL)
const searchPaymentPlanUnscheduledClient = new SearchPaymentPlanUnscheduledClient(SERVICE_URL)
const searchPaymentPlanForExpenseInvoiceClient = new SearchPaymentPlanForExpenseInvoiceClient(
  SERVICE_URL
)
const createExpensePaymentPlanClient = new CreateExpensePaymentPlanClient(SERVICE_URL)
const searchPaymentPlanForSaleInvoiceClient = new SearchPaymentPlanForSaleInvoiceClient(SERVICE_URL)
const createSalePaymentPlanClient = new CreateSalePaymentPlanClient(SERVICE_URL)
const getPaymentPlanClient = new GetPaymentPlanClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  isRedirectPage: false,
  error: null,
  isDeleted: false,
  excel: null,
  paymentPlan: null,
  unscheduled: null,
  expenseinvoice: null,
  saleinvoice: null,
  detail: null,
}

// Async Thunks
export const paymentPlanCreate = createAsyncThunk(
  'paymentplan/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createPaymentPlanClient.create(params)
      dispatch(setPaymentPlan(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanDelete = createAsyncThunk(
  'paymentplan/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deletePaymentPlanClient.delete(params)
      dispatch(setDeleted(params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanUpdate = createAsyncThunk(
  'paymentplan/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updatePaymentPlanClient.update(params)
      dispatch(setPaymentPlan(response || params))
      dispatch(setRedirectPage(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanUnscheduled = createAsyncThunk(
  'paymentplan/unscheduled',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPaymentPlanUnscheduledClient.search(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setUnscheduled(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanExpenseInvoiceGetAll = createAsyncThunk(
  'paymentplan/expenseInvoiceGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPaymentPlanForExpenseInvoiceClient.search(
        params.name,
        params.startDate,
        params.endDate,
        params.projectId,
        params.companyId,
        params.paymentPlanTypeId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setExpenseInvoice(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanExpenseInvoiceExcel = createAsyncThunk(
  'paymentplan/expenseInvoiceExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPaymentPlanForExpenseInvoiceClient.excel(
        params.name,
        params.startDate,
        params.endDate,
        params.projectId,
        params.companyId,
        params.paymentPlanTypeId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanExpenseInvoiceCreate = createAsyncThunk(
  'paymentplan/expenseInvoiceCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createExpensePaymentPlanClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanSaleInvoiceGetAll = createAsyncThunk(
  'paymentplan/saleInvoiceGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPaymentPlanForSaleInvoiceClient.search(
        params.name,
        params.startDate,
        params.endDate,
        params.projectId,
        params.companyId,
        params.paymentPlanTypeId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setSaleInvoice(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanSaleInvoiceExcel = createAsyncThunk(
  'paymentplan/saleInvoiceExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPaymentPlanForSaleInvoiceClient.excel(
        params.name,
        params.startDate,
        params.endDate,
        params.projectId,
        params.companyId,
        params.paymentPlanTypeId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanSaleInvoiceCreate = createAsyncThunk(
  'paymentplan/saleInvoiceCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createSalePaymentPlanClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentPlanExpenseSaleInvoiceGetId = createAsyncThunk(
  'paymentplan/expenseSaleInvoiceGetId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getPaymentPlanClient.get(id)
      dispatch(setDetail(response))
      dispatch(setRedirect(false))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const paymentplanSlice = createSlice({
  name: 'paymentplan',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setPaymentPlan: (state, action) => {
      state.paymentPlan = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirectPage: (state, action) => {
      state.isRedirectPage = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setUnscheduled: (state, action) => {
      state.unscheduled = action.payload
      state.loading = false
    },
    setExpenseInvoice: (state, action) => {
      state.expenseinvoice = action.payload
      state.loading = false
    },
    setSaleInvoice: (state, action) => {
      state.saleinvoice = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('paymentplan/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setPaymentPlan,
  setDeleted,
  setRedirectPage,
  setRedirect,
  setDetail,
  setUnscheduled,
  setExpenseInvoice,
  setSaleInvoice,
  setExcel,
  resetState,
} = paymentplanSlice.actions

// Selectors
export const selectPaymentPlanLoading = (state) => state.paymentplan.loading
export const selectPaymentPlanError = (state) => state.paymentplan.error
export const selectPaymentPlan = (state) => state.paymentplan.paymentPlan
export const selectPaymentPlanDeleted = (state) => state.paymentplan.isDeleted
export const selectPaymentPlanRedirectPage = (state) => state.paymentplan.isRedirectPage
export const selectPaymentPlanRedirect = (state) => state.paymentplan.isRedirect
export const selectPaymentPlanDetail = (state) => state.paymentplan.detail
export const selectPaymentPlanUnscheduled = (state) => state.paymentplan.unscheduled
export const selectPaymentPlanExpenseInvoice = (state) => state.paymentplan.expenseinvoice
export const selectPaymentPlanSaleInvoice = (state) => state.paymentplan.saleinvoice
export const selectPaymentPlanExcel = (state) => state.paymentplan.excel

export default paymentplanSlice.reducer
